import type { Ref } from 'react'
import { useTranslation } from '../../hooks/useTranslation'
import Money from '../../design/Money'
import { groupAddOns } from '../../lib/groupAddOns'
import type { CartAddOn, CartItem } from '../../types/display'

/** The cashier shows a repeated add-on as "2 x Bacon"; a customer checking
 *  their order should see the same count. */
function addOnLabel(addOn: CartAddOn): string {
  return addOn.quantity > 1 ? `${addOn.quantity} × ${addOn.title}` : addOn.title
}

export default function CartItemRow({
  item,
  currency,
  ref,
}: {
  item: CartItem
  currency: string
  ref?: Ref<HTMLDivElement>
}) {
  const { t } = useTranslation()

  const groups = groupAddOns(item.addOns)
  const removed = item.removedIngredients ?? []

  return (
    <div
      ref={ref}
      className="grid motion-safe:animate-itemIn grid-cols-[auto_1fr_auto] items-start gap-x-cfd-3 border-b border-divider/70 px-cfd-gutter py-cfd-2 last:border-transparent"
    >
      {/* Quantity first and solid, so "2 lattes" reads before the name.
          Keyed on the number, so a second scan remounts it and it bumps. */}
      <div
        key={item.quantity}
        className="min-w-[2.4em] rounded-full bg-primary px-[0.55em] py-[0.12em] text-center font-volte-semibold text-cfd-detail tabular-nums text-white motion-safe:animate-bump"
      >
        ×{item.quantity}
      </div>

      <div className="flex min-w-0 flex-col space-y-[0.2em]">
        <div className="font-volte-semibold text-cfd-row text-text-hi">{item.title}</div>

        {groups.map((group, i) => (
          <div key={`${group.categoryTitle ?? 'plain'}-${i}`} className="text-cfd-detail text-text-mid">
            {/* Mirrors the cashier's "Ekstra til pizza: Ekstra ost, Ekstra
                pepperoni". Without a category the add-ons read as a plain list. */}
            {group.categoryTitle && <span className="text-text-mid">{group.categoryTitle}: </span>}
            {group.addOns.map(addOnLabel).join(', ')}
          </div>
        ))}

        {removed.length > 0 && (
          <div className="text-cfd-detail text-text-mid">
            <span>{t('without-ingredients')}: </span>
            {/* Struck through, so "no tomato" reads as heard at a glance. */}
            {removed.map((ingredient, i) => (
              <span key={`${ingredient}-${i}`}>
                {i > 0 && ', '}
                <span className="line-through decoration-text-mid/70 decoration-2">{ingredient}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="text-right font-volte text-cfd-row tabular-nums text-text-hi">
        <Money amount={item.cost} currency={currency} />
      </div>
    </div>
  )
}
